import React, { useState, useEffect, useRef, useMemo } from "react";

/**
 * A component that simulates a streaming/typing effect for a given text.
 * It handles HTML content by rendering it incrementally.
 */
const StreamingText = ({ text, speed = 40, onComplete, highlightFn, paused = false, shouldStream = true }) => {
    const [displayedText, setDisplayedText] = useState(shouldStream ? "" : text);
    const currentIndexRef = useRef(0);
    const hasCompletedRef = useRef(!shouldStream);

    // Stable words array to avoid unnecessary effect triggers
    const words = useMemo(() => text ? text.split(" ") : [], [text]);

    // Store current onComplete in a ref to avoid restart loops in Parent
    const onCompleteRef = useRef(onComplete);
    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    // Reset when text changes
    useEffect(() => {
        if (shouldStream) {
            setDisplayedText("");
            currentIndexRef.current = 0;
            hasCompletedRef.current = false;
        } else {
            setDisplayedText(text);
            hasCompletedRef.current = true;
        }
    }, [text, shouldStream]);

    useEffect(() => {
        if (!shouldStream) return;
        if (!text || paused || hasCompletedRef.current) return;

        const interval = setInterval(() => {
            if (currentIndexRef.current < words.length) {
                const index = currentIndexRef.current;
                const nextWord = words[index];
                setDisplayedText(prev => prev + (index === 0 ? "" : " ") + nextWord);
                currentIndexRef.current++;
            } else if (currentIndexRef.current >= words.length && !hasCompletedRef.current) {
                clearInterval(interval);
                hasCompletedRef.current = true;
                if (onCompleteRef.current) onCompleteRef.current();
            } else {
                clearInterval(interval);
            }
        }, speed);

        return () => clearInterval(interval);
    }, [words, speed, paused, text, shouldStream]);

    // Apply highlighting if a function is provided
    const content = highlightFn ? highlightFn(displayedText) : displayedText;

    return (
        <div
            className="streaming-text conversation-txt"
            dangerouslySetInnerHTML={{ __html: content }}
        />
    );
};

export default StreamingText;
